/**
 * Project Gallery Overlap Parallax & Entrance Reveal
 * 
 * Features:
 * - 100% Unzoomed / Full Image Display: Images remain completely uncropped and whole
 * - Pegadas at rest (0px overlap): Images touch edge-to-edge naturally
 * - Overlap on scroll up ("se superponen cuando suben un poco"):
 *   As an image travels up the screen, it smoothly slides 35px-55px over the preceding image
 * - Smooth Progressive Entrance: Silky upward glide and fade-in
 */

export function initProjectParallax() {
   const items = Array.from(document.querySelectorAll('.js-project-item'));
   if (!items.length) return;

   const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

   // ------------------------------------------------------------------------
   // 1. SMOOTH PROGRESSIVE ENTRANCE REVEAL
   // ------------------------------------------------------------------------
   if ('IntersectionObserver' in window) {
      const revealObserver = new IntersectionObserver((entries, observer) => {
         entries.forEach(entry => {
            if (entry.isIntersecting) {
               const media = entry.target.querySelector('.js-project-media');
               if (media) {
                  media.classList.add('is-in-view');
               }
               observer.unobserve(entry.target);
            }
         });
      }, {
         rootMargin: '0px 0px -40px 0px',
         threshold: 0.05
      });

      items.forEach((it, idx) => {
         const media = it.querySelector('.js-project-media');
         if (!media) return;

         if (idx === 0) {
            media.classList.add('is-in-view');
         } else {
            const rect = it.getBoundingClientRect();
            if (rect.top < window.innerHeight && rect.bottom > 0) {
               media.classList.add('is-in-view');
            } else {
               revealObserver.observe(it);
            }
         }
      });
   } else {
      items.forEach(it => {
         const media = it.querySelector('.js-project-media');
         if (media) media.classList.add('is-in-view');
      });
   }

   // ------------------------------------------------------------------------
   // 2. CUMULATIVE OVERLAP (SIEMPRE PEGADAS, NUNCA SE DESPEGAN)
   // ------------------------------------------------------------------------
   if (!prefersReducedMotion) {
      let isTicking = false;

      function updateOverlap() {
         const windowHeight = window.innerHeight;
         const isMobile = window.innerWidth < 768;
         const scrollY = window.pageYOffset || document.documentElement.scrollTop;

         // Overlap amounts for each consecutive pair (delta[i] is overlap of item i over item i-1)
         const deltas = new Array(items.length).fill(0);

         for (let i = 1; i < items.length; i++) {
            const item = items[i];
            const cols = parseInt(item.dataset.cols || '12', 10);

            // Tailored overlap amount: "un poco" (35px to 55px)
            let maxDelta = 45;
            switch (cols) {
               case 2: maxDelta = 35; break;
               case 4: maxDelta = 40; break;
               case 6: maxDelta = 45; break;
               case 8: maxDelta = 50; break;
               case 10: maxDelta = 50; break;
               case 12: maxDelta = 55; break;
               default: maxDelta = 45; break;
            }
            if (isMobile) {
               maxDelta = Math.round(maxDelta * 0.5);
            }

            // Natural static position of the seam where item i meets item i-1
            const seamNaturalTop = item.offsetTop;

            // Trigger window: starts when seam reaches lower part of screen,
            // reaches full overlap when seam moves up
            const startScroll = Math.max(0, seamNaturalTop - windowHeight * 0.85);
            const travelDist = Math.max(160, windowHeight * 0.45);

            let progress = 0;
            if (scrollY <= startScroll) {
               progress = 0;
            } else if (scrollY >= startScroll + travelDist) {
               progress = 1;
            } else {
               progress = (scrollY - startScroll) / travelDist;
            }

            deltas[i] = Math.round(progress * maxDelta);
         }

         // CUMULATIVE PROPAGATION:
         // Item 0: 0px
         // Item 1: -deltas[1]
         // Item 2: -(deltas[1] + deltas[2])
         // Item 3: -(deltas[1] + deltas[2] + deltas[3])
         //
         // Mathematical property:
         // Distance between bottom of (i-1) and top of i = deltas[i] >= 0 at all times.
         // A gap (> 0) can NEVER exist. NUNCA SE DESPEGAN.
         let cumulativeShift = 0;
         items.forEach((item, idx) => {
            cumulativeShift += deltas[idx];
            const overlapY = cumulativeShift * -1;
            item.style.setProperty('--overlap-y', `${overlapY}px`);
         });

         isTicking = false;
      }

      function onScroll() {
         if (!isTicking) {
            requestAnimationFrame(updateOverlap);
            isTicking = true;
         }
      }

      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });

      // Initial run (strictly pegadas at scroll = 0)
      updateOverlap();
   }
}






